import React, { useEffect, useRef, useState } from 'react';
import { createModule } from '../../api/modules';
import Dropdown from '../../components/Dropdown/Dropdown';
import { useI18n } from '../../i18n/I18nContext';

const NEW_MODULE = '__new__';
// The backend's CreateModuleDto wants at least 3 characters; saying so here beats its generic 400.
const MODULE_TITLE_MIN = 3;

/**
 * The module of a lesson, with "+ New module" at the end of the list: the module is created right
 * here (no trip to the Modules section) and picked at once. `modules` are the course's modules;
 * `onCreated(module)` lets the page reload its list. `loadFailed` shows why the list is empty.
 */
function ModulePicker({ courseId, modules, value, onChange, onCreated, loadFailed, onRetryLoad, labelledBy }) {
  const { t } = useI18n();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // After the commit, so it wins over the Dropdown handing focus back to its own button.
  useEffect(() => {
    if (creating) inputRef.current?.focus();
  }, [creating]);

  const handlePick = (next) => {
    if (next === NEW_MODULE) {
      setCreating(true);
      setTitle('');
      setError(null);
      return;
    }
    onChange(next);
  };

  const create = async () => {
    const name = title.trim();
    if (name.length < MODULE_TITLE_MIN) {
      setError(t('lessonAuthoring.module.tooShort'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const nextOrder = modules.reduce((max, m) => Math.max(max, Number(m.orderIndex) || 0), -1) + 1;
      const module = await createModule(courseId, { title: name, orderIndex: nextOrder });
      await onCreated?.(module);
      if (module?.id) onChange(module.id);
      setCreating(false);
    } catch {
      setError(t('lessonAuthoring.module.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="les-module-picker">
      <Dropdown
        value={value}
        onChange={handlePick}
        placeholder={t('courseContent.lessons.noModuleOption')}
        ariaLabel={labelledBy ? undefined : t('courseContent.lessons.moduleLabel')}
        labelledBy={labelledBy}
        options={[
          { value: '', label: t('courseContent.lessons.noModuleOption'), separator: true },
          ...modules.map((m) => ({
            value: m.id,
            label: m.title || m.name || t('courseContent.lessons.unnamedModule'),
          })),
          { value: NEW_MODULE, label: t('lessonAuthoring.module.newOption') },
        ]}
      />
      {loadFailed && (
        <p className="ui-error les-module-load-error" role="alert">
          {t('lessonAuthoring.module.loadFailed')}{' '}
          <button type="button" className="les-link-btn" onClick={onRetryLoad}>
            {t('lessonAuthoring.module.retry')}
          </button>
        </p>
      )}
      {creating && (
        <div className="les-module-new">
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              // Enter here creates the module; it must not submit the lesson form around it.
              if (e.key === 'Enter') {
                e.preventDefault();
                create();
              } else if (e.key === 'Escape') {
                e.stopPropagation();
                setCreating(false);
              }
            }}
            maxLength={100}
            aria-label={t('lessonAuthoring.module.newLabel')}
            aria-invalid={error ? 'true' : undefined}
            placeholder={t('lessonAuthoring.module.newPlaceholder')}
            disabled={busy}
          />
          <button type="button" className="ds-btn ds-btn-primary ds-btn-sm" onClick={create} disabled={busy}>
            {t('lessonAuthoring.module.create')}
          </button>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setCreating(false)} disabled={busy}>
            {t('lessonAuthoring.module.cancel')}
          </button>
          {error && <p className="ui-error les-module-new-error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}

export default ModulePicker;
